import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { RefObject, useEffect } from "react";
import { useStableCallback } from "./useStableCallback";

/** This function will not trigger a render, call setState in the onResize if needed */
export function useResizeObserver<T extends HTMLElement>(
	ref: RefObject<T | null | undefined>,
	onResize: (contentRect: DOMRectReadOnly, target: T) => void,
	/** Hooks cannot be run conditionally, with this boolean you can disable it */
	isDisabled?: boolean,
) {
	const stableOnResize = useStableCallback(onResize);

	useEffect(() => {
		const el = ref.current;
		if (!el || isDisabled) return undefined;

		const ResizeObserver = (window as any).ResizeObserver || ResizeObserverPolyfill;

		let rafId = 0;

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		const resizeObserver: ResizeObserverPolyfill = new ResizeObserver((entries: any) => {
			// wrap in requestAnimationFrame to safely ignore following error: ResizeObserver loop limit exceeded
			// https://stackoverflow.com/a/58701523/681803
			if (rafId) window.cancelAnimationFrame(rafId);
			rafId = window.requestAnimationFrame(() => {
				if (!Array.isArray(entries) || !entries.length) {
					return;
				}
				// We only observe 1 element
				const [entry] = entries;
				stableOnResize(entry.contentRect, entry.target);
			});
		});

		resizeObserver.observe(el);

		return () => {
			if (rafId) window.cancelAnimationFrame(rafId);
			rafId = 0;
			resizeObserver.unobserve(el);
		};
	}, [stableOnResize, ref, isDisabled]);
}
